import React from 'react';
import { Globe, Scale, Shield, Users } from 'lucide-react';
import { Card } from '../components/Card';
import { SEO } from '../components/SEO';
import { useLanguage } from '../hooks/useLanguage';
export function InternationalFrameworkPage() {
  const {
    t,
    language
  } = useLanguage();
  const frameworks = [{
    icon: Scale,
    titleKey: 'framework.un.title',
    title: {
      en: 'UN Convention against Cybercrime',
      id: 'Konvensi PBB melawan Kejahatan Dunia Maya',
      ms: 'Konvensyen PBB menentang Jenayah Siber',
      th: 'อนุสัญญาสหประชาชาติว่าด้วยอาชญากรรมไซเบอร์',
      vi: 'Công ước Liên Hợp Quốc chống Tội phạm mạng',
      fil: 'UN Convention laban sa Cybercrime',
      km: 'អនុសញ្ញាអង្គការសហប្រជាជាតិប្រឆាំងឧក្រិដ្ឋកម្មតាមអ៊ីនធឺណិត',
      lo: 'ສົນທິສັນຍາສະຫະປະຊາຊາດຕ້ານອາຊະຍາກຳທາງໄຊເບີ',
      my: 'ဆိုက်ဘာရာဇဝတ်မှုဆန့်ကျင်ရေး ကုလသမဂ္ဂ သဘောတူညီချက်'
    },
    organization: {
      en: 'United Nations',
      id: 'Perserikatan Bangsa-Bangsa',
      ms: 'Pertubuhan Bangsa-Bangsa Bersatu',
      th: 'สหประชาชาติ',
      vi: 'Liên Hợp Quốc',
      fil: 'United Nations',
      km: 'អង្គការសហប្រជាជាតិ',
      lo: 'ສະຫະປະຊາຊາດ',
      my: 'ကုလသမဂ္ဂ'
    },
    description: {
      en: 'International legal framework for cooperation in combating cybercrime, including online fraud and scams.',
      id: 'Kerangka hukum internasional untuk kerja sama dalam memerangi kejahatan dunia maya, termasuk penipuan online.',
      ms: 'Rangka kerja undang-undang antarabangsa untuk kerjasama dalam memerangi jenayah siber, termasuk penipuan dalam talian.',
      th: 'กรอบกฎหมายระหว่างประเทศสำหรับความร่วมมือในการต่อสู้กับอาชญากรรมไซเบอร์ รวมถึงการฉ้อโกงและการหลอกลวงออนไลน์',
      vi: 'Khung pháp lý quốc tế cho hợp tác chống tội phạm mạng, bao gồm gian lận và lừa đảo trực tuyến.',
      fil: 'International legal framework para sa kooperasyon sa paglaban sa cybercrime, kabilang ang online fraud at scam.',
      km: 'ក្របខ័ណ្ឌផ្លូវច្បាប់អន្តរជាតិសម្រាប់កិច្ចសហប្រតិបត្តិការក្នុងការប្រយុទ្ធប្រឆាំងឧក្រិដ្ឋកម្មតាមអ៊ីនធឺណិត រួមទាំងការបោកប្រាស់តាមអ៊ីនធឺណិត។',
      lo: 'ກອບກົດໝາຍສາກົນສຳລັບການຮ່ວມມືໃນການຕໍ່ສູ້ກັບອາຊະຍາກຳທາງໄຊເບີ, ລວມທັງການໂກງ ແລະ ການຫຼອກລວງອອນລາຍ.',
      my: 'အွန်လိုင်းလိမ်လည်မှုများ အပါအဝင် ဆိုက်ဘာရာဇဝတ်မှုကို တိုက်ဖျက်ရာတွင် ပူးပေါင်းဆောင်ရွက်ရန် နိုင်ငံတကာ ဥပဒေမူဘောင်။'
    },
    year: '2024',
    link: 'https://www.unodc.org/unodc/en/cybercrime/index.html'
  }, {
    icon: Shield,
    title: {
      en: 'UNTOC (UN Convention against Transnational Organized Crime)',
      id: 'UNTOC (Konvensi PBB melawan Kejahatan Terorganisir Transnasional)',
      ms: 'UNTOC (Konvensyen PBB menentang Jenayah Terancang Transnasional)',
      th: 'UNTOC (อนุสัญญาสหประชาชาติว่าด้วยอาชญากรรมข้ามชาติที่จัดตั้งขึ้น)',
      vi: 'UNTOC (Công ước Liên Hợp Quốc chống Tội phạm có Tổ chức Xuyên quốc gia)',
      fil: 'UNTOC (UN Convention laban sa Transnational Organized Crime)',
      km: 'UNTOC (អនុសញ្ញាអង្គការសហប្រជាជាតិប្រឆាំងឧក្រិដ្ឋកម្មរៀបចំឆ្លងដែន)',
      lo: 'UNTOC (ສົນທິສັນຍາສະຫະປະຊາຊາດຕ້ານອາຊະຍາກຳຂ້າມຊາດທີ່ຈັດຕັ້ງ)',
      my: 'UNTOC (နိုင်ငံဖြတ်ကျော် ဖွဲ့စည်းထားသော ရာဇဝတ်မှုဆန့်ကျင်ရေး ကုလသမဂ္ဂ သဘောတူညီချက်)'
    },
    organization: {
      en: 'United Nations',
      id: 'Perserikatan Bangsa-Bangsa',
      ms: 'Pertubuhan Bangsa-Bangsa Bersatu',
      th: 'สหประชาชาติ',
      vi: 'Liên Hợp Quốc',
      fil: 'United Nations',
      km: 'អង្គការសហប្រជាជាតិ',
      lo: 'ສະຫະປະຊາຊາດ',
      my: 'ကုလသမဂ္ဂ'
    },
    description: {
      en: 'Addresses organized criminal groups operating across borders, including cybercrime syndicates.',
      id: 'Menangani kelompok kriminal terorganisir yang beroperasi lintas batas, termasuk sindikat kejahatan dunia maya.',
      ms: 'Menangani kumpulan jenayah terancang yang beroperasi merentasi sempadan, termasuk sindiket jenayah siber.',
      th: 'จัดการกับกลุ่มอาชญากรที่จัดตั้งขึ้นซึ่งดำเนินการข้ามพรมแดน รวมถึงกลุ่มอาชญากรไซเบอร์',
      vi: 'Giải quyết các nhóm tội phạm có tổ chức hoạt động xuyên biên giới, bao gồm các băng nhóm tội phạm mạng.',
      fil: 'Tinutugunan ang mga organized criminal group na nagpapatakbo sa mga hangganan, kabilang ang mga cybercrime syndicate.',
      km: 'ដោះស្រាយក្រុមឧក្រិដ្ឋជនដែលរៀបចំដែលប្រតិបត្តិការឆ្លងដែន រួមទាំងក្រុមឧក្រិដ្ឋកម្មតាមអ៊ីនធឺណិត។',
      lo: 'ແກ້ໄຂກຸ່ມອາຊະຍາກອນທີ່ຈັດຕັ້ງທີ່ດຳເນີນງານຂ້າມຊາຍແດນ, ລວມທັງກຸ່ມອາຊະຍາກຳທາງໄຊເບີ.',
      my: 'ဆိုက်ဘာရာဇဝတ်အဖွဲ့များ အပါအဝင် နယ်စပ်ဖြတ်ကျော် လုပ်ကိုင်နေသော ဖွဲ့စည်းထားသော ရာဇဝတ်အဖွဲ့များကို ကိုင်တွယ်ဖြေရှင်းခြင်း။'
    },
    year: '2000',
    link: 'https://www.unodc.org/unodc/en/organized-crime/intro/UNTOC.html'
  }, {
    icon: Globe,
    title: {
      en: 'ASEAN Cybersecurity Cooperation Strategy',
      id: 'Strategi Kerja Sama Keamanan Siber ASEAN',
      ms: 'Strategi Kerjasama Keselamatan Siber ASEAN',
      th: 'ยุทธศาสตร์ความร่วมมือด้านความปลอดภัยทางไซเบอร์ของอาเซียน',
      vi: 'Chiến lược Hợp tác An ninh mạng ASEAN',
      fil: 'ASEAN Cybersecurity Cooperation Strategy',
      km: 'យុទ្ធសាស្រ្តសហប្រតិបត្តិការសុវត្ថិភាពតាមអ៊ីនធឺណិតអាស៊ីអាគ្នេយ៍',
      lo: 'ຍຸດທະສາດການຮ່ວມມືຄວາມປອດໄພທາງໄຊເບີອາຊຽນ',
      my: 'အာဆီယံ ဆိုက်ဘာလုံခြုံရေး ပူးပေါင်းဆောင်ရွက်မှု မဟာဗျူဟာ'
    },
    organization: {
      en: 'ASEAN',
      id: 'ASEAN',
      ms: 'ASEAN',
      th: 'อาเซียน',
      vi: 'ASEAN',
      fil: 'ASEAN',
      km: 'អាស៊ីអាគ្នេយ៍',
      lo: 'ອາຊຽນ',
      my: 'အာဆီယံ'
    },
    description: {
      en: 'Regional framework for cybersecurity cooperation and information sharing among ASEAN member states.',
      id: 'Kerangka regional untuk kerja sama keamanan siber dan berbagi informasi di antara negara-negara anggota ASEAN.',
      ms: 'Rangka kerja serantau untuk kerjasama keselamatan siber dan perkongsian maklumat di kalangan negara anggota ASEAN.',
      th: 'กรอบความร่วมมือระดับภูมิภาคด้านความปลอดภัยทางไซเบอร์และการแบ่งปันข้อมูลระหว่างประเทศสมาชิกอาเซียน',
      vi: 'Khung khu vực cho hợp tác an ninh mạng và chia sẻ thông tin giữa các quốc gia thành viên ASEAN.',
      fil: 'Regional framework para sa cybersecurity cooperation at information sharing sa mga miyembrong estado ng ASEAN.',
      km: 'ក្របខ័ណ្ឌតំបន់សម្រាប់កិច្ចសហប្រតិបត្តិការសុវត្ថិភាពតាមអ៊ីនធឺណិត និងការចែករំលែកព័ត៌មានក្នុងចំណោមរដ្ឋសមាជិកអាស៊ីអាគ្នេយ៍។',
      lo: 'ກອບພາກພື້ນສຳລັບການຮ່ວມມືຄວາມປອດໄພທາງໄຊເບີ ແລະ ການແບ່ງປັນຂໍ້ມູນລະຫວ່າງປະເທດສະມາຊິກອາຊຽນ.',
      my: 'အာဆီယံ အဖွဲ့ဝင်နိုင်ငံများအကြား ဆိုက်ဘာလုံခြုံရေး ပူးပေါင်းဆောင်ရွက်မှုနှင့် အချက်အလက်မျှဝေခြင်းအတွက် ဒေသတွင်း မူဘောင်။'
    },
    year: '2021',
    link: 'https://asean.org/'
  }, {
    icon: Users,
    title: {
      en: 'INTERPOL Cybercrime Programme',
      id: 'Program Kejahatan Siber INTERPOL',
      ms: 'Program Jenayah Siber INTERPOL',
      th: 'โปรแกรมอาชญากรรมไซเบอร์ของอินเตอร์โพล',
      vi: 'Chương trình Tội phạm mạng INTERPOL',
      fil: 'INTERPOL Cybercrime Programme',
      km: 'កម្មវិធីឧក្រិដ្ឋកម្មតាមអ៊ីនធឺណិត INTERPOL',
      lo: 'ໂຄງການອາຊະຍາກຳທາງໄຊເບີ INTERPOL',
      my: 'INTERPOL ဆိုက်ဘာရာဇဝတ်မှု အစီအစဉ်'
    },
    organization: {
      en: 'INTERPOL',
      id: 'INTERPOL',
      ms: 'INTERPOL',
      th: 'อินเตอร์โพล',
      vi: 'INTERPOL',
      fil: 'INTERPOL',
      km: 'INTERPOL',
      lo: 'INTERPOL',
      my: 'INTERPOL'
    },
    description: {
      en: 'Global police cooperation to combat cybercrime, including financial fraud and online scams.',
      id: 'Kerja sama polisi global untuk memerangi kejahatan dunia maya, termasuk penipuan keuangan dan penipuan online.',
      ms: 'Kerjasama polis global untuk memerangi jenayah siber, termasuk penipuan kewangan dan penipuan dalam talian.',
      th: 'ความร่วมมือของตำรวจทั่วโลกเพื่อต่อสู้กับอาชญากรรมไซเบอร์ รวมถึงการฉ้อโกงทางการเงินและการหลอกลวงออนไลน์',
      vi: 'Hợp tác cảnh sát toàn cầu để chống tội phạm mạng, bao gồm gian lận tài chính và lừa đảo trực tuyến.',
      fil: 'Global police cooperation upang labanan ang cybercrime, kabilang ang financial fraud at online scam.',
      km: 'កិច្ចសហប្រតិបត្តិការប៉ូលីសពិភពលោកដើម្បីប្រយុទ្ធប្រឆាំងឧក្រិដ្ឋកម្មតាមអ៊ីនធឺណិត រួមទាំងការបោកប្រាស់ហិរញ្ញវត្ថុ និងការបោកប្រាស់តាមអ៊ីនធឺណិត។',
      lo: 'ການຮ່ວມມືຕຳຫຼວດທົ່ວໂລກເພື່ອຕໍ່ສູ້ກັບອາຊະຍາກຳທາງໄຊເບີ, ລວມທັງການໂກງທາງການເງິນ ແລະ ການຫຼອກລວງອອນລາຍ.',
      my: 'ငွေကြေးလိမ်လည်မှုနှင့် အွန်လိုင်းလိမ်လည်မှုများ အပါအဝင် ဆိုက်ဘာရာဇဝတ်မှုကို တိုက်ဖျက်ရန် ကမ္ဘာလုံးဆိုင်ရာ ရဲတပ်ဖွဲ့ ပူးပေါင်းဆောင်ရွက်မှု။'
    },
    year: 'Ongoing',
    link: 'https://www.interpol.int/en/Crimes/Cybercrime'
  }];
  return <div className="container mx-auto px-4 py-12 max-w-5xl" data-id="element-969">
      <SEO title={t('seo.framework.title')} description={t('seo.framework.desc')} data-id="element-970" />
      <div className="text-center mb-12 animate-fade-in" data-id="element-971">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-6" data-id="element-972">
          <Globe className="h-8 w-8 text-primary" data-id="element-973" />
        </div>
        <h1 className="text-4xl font-bold mb-4" data-id="element-974">{t('framework.title')}</h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto" data-id="element-975">
          {t('framework.subtitle')}
        </p>
      </div>

      <div className="space-y-6 mb-12" data-id="element-976">
        {frameworks.map((framework, index) => {
        const Icon = framework.icon;
        return <Card key={index} className="hover:shadow-soft-lg transition-all animate-slide-up" style={{
          animationDelay: `${index * 100}ms`
        }} data-id="element-977">
              <div className="flex items-start space-x-6" data-id="element-978">
                <div className="p-4 bg-primary/5 rounded-2xl flex-shrink-0" data-id="element-979">
                  <Icon className="h-8 w-8 text-primary" data-id="element-980" />
                </div>
                <div className="flex-1" data-id="element-981">
                  <div className="flex items-start justify-between mb-3" data-id="element-982">
                    <div data-id="element-983">
                      <h3 className="text-xl font-bold mb-1" data-id="element-984">
                        {framework.title[language] || framework.title['en']}
                      </h3>
                      <p className="text-sm text-muted-foreground" data-id="element-985">
                        {framework.organization[language] || framework.organization['en']}{' '}
                        • {framework.year}
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-4" data-id="element-986">
                    {framework.description[language] || framework.description['en']}
                  </p>
                  <a href={framework.link} target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline text-sm" data-id="element-987">
                    {t('action.learnMore')} →
                  </a>
                </div>
              </div>
            </Card>;
      })}
      </div>

      {/* Principles Section */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100" data-id="element-988">
        <h2 className="text-2xl font-bold mb-6 text-blue-900" data-id="element-989">
          {t('framework.principles.title')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-990">
          <div data-id="element-991">
            <h3 className="font-semibold text-blue-900 mb-2" data-id="element-992">
              {t('framework.principle.victim.title')}
            </h3>
            <p className="text-blue-800 text-sm leading-relaxed" data-id="element-993">
              {t('framework.principle.victim.desc')}
            </p>
          </div>
          <div data-id="element-994">
            <h3 className="font-semibold text-blue-900 mb-2" data-id="element-995">
              {t('framework.principle.cooperation.title')}
            </h3>
            <p className="text-blue-800 text-sm leading-relaxed" data-id="element-996">
              {t('framework.principle.cooperation.desc')}
            </p>
          </div>
          <div data-id="element-997">
            <h3 className="font-semibold text-blue-900 mb-2" data-id="element-998">
              {t('framework.principle.prevention.title')}
            </h3>
            <p className="text-blue-800 text-sm leading-relaxed" data-id="element-999">
              {t('framework.principle.prevention.desc')}
            </p>
          </div>
          <div data-id="element-1000">
            <h3 className="font-semibold text-blue-900 mb-2" data-id="element-1001">
              {t('framework.principle.legal.title')}
            </h3>
            <p className="text-blue-800 text-sm leading-relaxed" data-id="element-1002">
              {t('framework.principle.legal.desc')}
            </p>
          </div>
        </div>
      </Card>
    </div>;
}